/* eslint-disable react/no-multi-comp -- don't want to create separate files for this simple adapter*/
import {
    Arrow,
    Content as RadixContent,
    Portal,
    Root,
    Trigger as RadixTrigger,
} from '@radix-ui/react-popover';
import React, {useId} from 'react';
import {createGlobalStyle} from 'styled-components';

// NOTE: I have only added props that I have used refer to this documentation
// if any more props are to be used.
// https://www.radix-ui.com/primitives/docs/components/popover

interface PopoverProps {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    children: React.ReactNode;
}

export const Popover = ({open, onOpenChange, children}: PopoverProps) => {
    return (
        <Root open={open} onOpenChange={onOpenChange}>
            {children}
        </Root>
    );
};

export const Trigger = ({
    children,
    className,
    onClick,
}: {
    children: React.ReactNode;
    className?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
}) => {
    return (
        <RadixTrigger className={className} onClick={onClick}>
            {children}
        </RadixTrigger>
    );
};

// Radix's Popper wrapper (the actual positioned/portalled element) sets its
// own inline z-index by mirroring the content's computed z-index on mount, so
// styling the content directly is unreliable. We target the wrapper instead,
// scoped to this instance via a stable data attribute (a className would get
// clobbered whenever a consumer does `styled(Content)`).
const WrapperZIndex = createGlobalStyle<{$popoverId: string; $zIndex: number}>`
    [data-radix-popper-content-wrapper]:has(
        [data-popover-id='${({$popoverId}) => $popoverId}']
    ) {
        z-index: ${({$zIndex}) => $zIndex} !important;
    }
`;

interface ContentProps {
    children: React.ReactNode;
    side?: 'top' | 'right' | 'bottom' | 'left';
    sideOffset?: number;
    className?: string;
    alignOffset?: number;
    collisionPadding?: number;
    zIndex?: number;
    container?: Element | null;
}

export const Content = ({
    children,
    side = 'bottom',
    sideOffset = 8,
    className = 'qa-popover-content',
    zIndex,
    collisionPadding,
    container,
}: ContentProps) => {
    const popoverId = useId();

    return (
        <Portal container={container}>
            <>
                {zIndex !== undefined && (
                    <WrapperZIndex $popoverId={popoverId} $zIndex={zIndex} />
                )}
                <RadixContent
                    data-popover-id={popoverId}
                    side={side}
                    sideOffset={sideOffset}
                    collisionPadding={collisionPadding}
                    className={className}>
                    {children}
                    <Arrow className="qa-popover-arrow" />
                </RadixContent>
            </>
        </Portal>
    );
};

/* eslint-enable react/no-multi-comp -- don't want to create separate files for this simple adapter */
